import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import { useEffect, useRef, useState } from 'react';
import { Transforms } from 'slate';
import { saveAs } from 'file-saver';
import {
  ReactEditor,
  useFocused,
  useSelected,
  useSlateStatic
} from 'slate-react';
import { LoaderTraceLogo } from '@stratumn/atomic';
import { cn } from '@/shadcn/lib/utils';
import { Button } from '@/shadcn/button';
import { Download, Trash } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider } from '@/shadcn/tooltip';
import { TooltipTrigger } from '@radix-ui/react-tooltip';
import { ElementProps } from '@/components/ui/richEditor/elements/elements.richEditor.types';
import { downloadFile } from '@/utils/downloadFile';

const fileToBase64 = (file: File, media: any): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64Data = (reader.result as string).split(',')[1];
      const base64Url = `data:${media.mimetype};base64,${base64Data}`;
      resolve(base64Url);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const ImageComponent = ({
  attributes,
  children,
  element,
  editorInitiated,
  readOnly = false
}: ElementProps) => {
  const editor = useSlateStatic();
  const path = ReactEditor.findPath(editor, element);
  const [loading, setLoading] = useState(true);
  const [dataUrl, setDataUrl] = useState<string | undefined>();
  const [tempUrl] = useState<string>(element.tempUrl);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const selected = useSelected();
  const focused = useFocused();

  const url = dataUrl || tempUrl;

  useEffect(() => {
    if (tempUrl && element.media) {
      // If the image has a tempUrl, it means that the image was just added by the user
      // Once it also has a media object defined we know the file has been uploaded, so we can remove the loading state
      setLoading(false);
    }
    if (editorInitiated && element.media && !tempUrl) {
      // This case happens when the image component is created without a tempUrl (when the editor is used to load an existing comment whith an already uploaded image)
      // This means that the image was already uploaded and we need to download it.
      downloadFile(
        element.media,
        setLoading,
        false,
        file => {
          fileToBase64(file, element.media).then(base64Url => {
            // Set the component's local dataUrl
            setDataUrl(base64Url);
          });
        },
        false
      );
    }
  }, [element.media, tempUrl]);

  const handleDownload = () => {
    saveAs(url, element.media?.name);
  };

  const handleRemove = () => {
    Transforms.removeNodes(editor, { at: path });
  };

  return (
    <Zoom>
      <div {...attributes} className="flex w-full justify-center p-2">
        {children}
        <div contentEditable={false} className="group/image relative w-fit">
          <img
            ref={imgRef}
            src={url}
            alt=""
            className={cn(
              'focus-within:outline-primary block max-h-80 max-w-full cursor-zoom-in rounded-md outline-2 outline-transparent',
              selected && focused && 'outline-primary',
              loading && 'animate-pulse'
            )}
          />
          <div className="bg-accent/30 absolute top-2 right-2 flex gap-2 rounded-md p-1 opacity-0 backdrop-blur-sm transition-opacity group-hover/image:opacity-100">
            <TooltipProvider>
              {url && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={handleDownload}
                      variant="ghost"
                      size="smallIcon"
                    >
                      <Download />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Download image</TooltipContent>
                </Tooltip>
              )}
              {!readOnly && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={handleRemove}
                      variant="ghost"
                      size="smallIcon"
                    >
                      <Trash />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Delete image</TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
          </div>
          {loading && (
            <div className="absolute top-0 flex size-full items-center justify-center">
              <LoaderTraceLogo />
            </div>
          )}
        </div>
      </div>
    </Zoom>
  );
};
